/**
 * Page de liste des inscriptions pour les ADMINS avec filtres avancés.
 * Affiche uniquement les inscriptions filtrées (pas de chargement sans filtre).
 */
import { useState, useEffect } from 'react';
import { inscriptionService, sejourService, getMediaUrl } from '../services/api';
import { notify } from '../components/NotificationContainer';
import logger from '../utils/logger';

const InscriptionsListAdmin = () => {
  // État des données
  const [inscriptions, setInscriptions] = useState([]);
  const [inscriptionsArchivees, setInscriptionsArchivees] = useState([]);
  const [sejours, setSejours] = useState([]);
  const [tranchesAge, setTranchesAge] = useState([]);
  const [loading, setLoading] = useState(false);

  // État des filtres
  const [filtres, setFiltres] = useState({
    sejour_id: '',
    tranche_age: '',
    afficher_annules: false,
    afficher_archives: false,
  });

  // État pour la recherche par nom/prénom
  const [modeRecherche, setModeRecherche] = useState('sejour'); // 'sejour' ou 'enfant'
  const [rechercheEnfant, setRechercheEnfant] = useState('');
  const [rechercheEnCours, setRechercheEnCours] = useState(false);

  const [filtresAppliques, setFiltresAppliques] = useState(false);

  // Charger uniquement la liste des séjours au montage (pas les inscriptions)
  useEffect(() => {
    loadSejours();
  }, []);

  const loadSejours = async () => {
    try {
      const response = await sejourService.getAll();
      // Gérer le cas où response.data peut être un objet avec results ou un tableau direct
      const sejoursData = response.data?.results || response.data || [];
      setSejours(Array.isArray(sejoursData) ? sejoursData : []);
    } catch (error) {
      logger.error('Erreur lors du chargement des séjours:', error);
      notify.error('Erreur lors du chargement des séjours');
    }
  };


  // Charger les tranches d'âge quand un séjour est sélectionné
  useEffect(() => {
    if (filtres.sejour_id) {
      const sejourSelectionne = sejours.find(s => s.id === parseInt(filtres.sejour_id));
      if (sejourSelectionne && sejourSelectionne.tranches_prix && sejourSelectionne.tranches_prix.length > 0) {
        setTranchesAge(sejourSelectionne.tranches_prix);
      } else {
        setTranchesAge([]);
      }
    } else {
      setTranchesAge([]);
    }
  }, [filtres.sejour_id, sejours]);

  // Fonction pour la recherche par nom/prénom avec debounce
  useEffect(() => {
    if (modeRecherche !== 'enfant') return;

    // Valider minimum 3 caractères
    if (rechercheEnfant.length > 0 && rechercheEnfant.length < 3) {
      return;
    }

    if (!rechercheEnfant) {
      // Réinitialiser si vide
      setInscriptions([]);
      setInscriptionsArchivees([]);
      setFiltresAppliques(false);
      return;
    }

    // Debounce: attendre 500ms après la dernière frappe
    const timeoutId = setTimeout(() => {
      rechercherParEnfant(rechercheEnfant);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheEnfant, modeRecherche, filtres.afficher_annules, filtres.afficher_archives]);

  const rechercherParEnfant = async (query) => {
    setRechercheEnCours(true);
    setLoading(true);
    setFiltresAppliques(true);

    try {
      const params = { q: query };

      if (filtres.afficher_annules) {
        params.afficher_annules = 'true';
      }

      if (filtres.afficher_archives) {
        params.afficher_archives = 'true';
      }

      const response = await inscriptionService.rechercheEnfant(params);

      if (filtres.afficher_archives) {
        setInscriptionsArchivees(response.data.inscriptions_archivees || []);
        setInscriptions([]);
      } else {
        setInscriptions(response.data.inscriptions || []);
        setInscriptionsArchivees([]);
      }
    } catch (error) {
      logger.error('Erreur lors de la recherche:', error);
      if (error.response?.status === 400) {
        notify.warning(error.response.data.error || 'Veuillez entrer au moins 3 caractères');
      } else {
        notify.error('Erreur lors de la recherche');
      }
      setInscriptions([]);
      setInscriptionsArchivees([]);
    } finally {
      setLoading(false);
      setRechercheEnCours(false);
    }
  };

  const appliquerFiltres = async () => {
    // Mode recherche par enfant
    if (modeRecherche === 'enfant') {
      if (!rechercheEnfant || rechercheEnfant.length < 3) {
        notify.warning('Veuillez entrer au moins 3 caractères pour rechercher un enfant');
        return;
      }
      await rechercherParEnfant(rechercheEnfant);
      return;
    }

    // Mode recherche par séjour (original)
    if (!filtres.sejour_id) {
      notify.warning('Veuillez sélectionner un séjour');
      return;
    }

    setLoading(true);
    setFiltresAppliques(true);

    try {
      const params = {
        sejour_id: filtres.sejour_id,
      };

      if (filtres.tranche_age) {
        params.tranche_age = filtres.tranche_age;
      }

      if (filtres.afficher_annules) {
        params.afficher_annules = 'true';
      }

      if (filtres.afficher_archives) {
        params.afficher_archives = 'true';
      }

      const response = await inscriptionService.getInscriptionsFiltrees(params);

      if (filtres.afficher_archives) {
        setInscriptionsArchivees(response.data.inscriptions_archivees || []);
        setInscriptions([]);
      } else {
        setInscriptions(response.data.inscriptions || []);
        setInscriptionsArchivees([]);
      }
    } catch (error) {
      logger.error('Erreur lors du chargement des inscriptions filtrées:', error);
      notify.error('Erreur lors du chargement des inscriptions');
      setInscriptions([]);
      setInscriptionsArchivees([]);
    } finally {
      setLoading(false);
    }
  };

  const reinitialiserFiltres = () => {
    setFiltres({
      sejour_id: '',
      tranche_age: '',
      afficher_annules: false,
      afficher_archives: false,
    });
    setRechercheEnfant('');
    setModeRecherche('sejour');
    setInscriptions([]);
    setInscriptionsArchivees([]);
    setFiltresAppliques(false);
  };

  // Calculer l'âge de l'enfant à la date de début du séjour
  const calculateAgeAtSejour = (dateNaissance, dateDebut) => {
    if (!dateNaissance || !dateDebut) return null;

    const birthDate = new Date(dateNaissance);
    const sejourDate = new Date(dateDebut);

    let age = sejourDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = sejourDate.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && sejourDate.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  // Vérifier si l'âge de l'enfant est hors de la tranche d'âge du séjour
  // OU si l'enfant est inscrit dans une tranche d'âge différente de son âge réel
  const isAgeOutOfRange = (inscription) => {
    if (!inscription.enfant_details?.date_naissance || !inscription.sejour_details?.date_debut) {
      return false;
    }

    const ageAtSejour = calculateAgeAtSejour(
      inscription.enfant_details.date_naissance,
      inscription.sejour_details.date_debut
    );

    if (ageAtSejour === null) return false;

    // Vérifier si l'enfant est inscrit dans une tranche spécifique
    // Utiliser tranche_prix_details en priorité, puis les champs figés inscription_age_min/max
    if (inscription.tranche_prix_details) {
      // Cas 1: L'enfant est inscrit dans une tranche qui ne correspond pas à son âge
      const trancheMin = inscription.tranche_prix_details.age_min;
      const trancheMax = inscription.tranche_prix_details.age_max;

      if (ageAtSejour < trancheMin || ageAtSejour > trancheMax) {
        return true; // L'âge de l'enfant ne correspond pas à la tranche dans laquelle il est inscrit
      }
    } else if (inscription.inscription_age_min !== null && inscription.inscription_age_max !== null) {
      // Fallback sur les champs figés si la tranche n'existe plus (supprimée)
      const trancheMin = inscription.inscription_age_min;
      const trancheMax = inscription.inscription_age_max;

      if (ageAtSejour < trancheMin || ageAtSejour > trancheMax) {
        return true; // L'âge de l'enfant ne correspond pas à la tranche archivée
      }
    }

    // Cas 2: Vérifier si l'âge est hors de la tranche globale du séjour
    const ageMin = inscription.sejour_details?.age_min;
    const ageMax = inscription.sejour_details?.age_max;

    return ageAtSejour < ageMin || ageAtSejour > ageMax;
  };

  const getStatutLabel = (statut) => {
    switch (statut) {
      case 'CONFIRMEE':
        return 'Confirmée';
      case 'EN_ATTENTE':
        return 'En attente';
      case 'EN_ATTENTE_VALIDATION_PARTENAIRE':
        return 'En attente validation';
      case 'TERMINE':
        return 'Terminée';
      case 'ANNULEE':
        return 'Annulée';
      default:
        return statut;
    }
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'CONFIRMEE':
        return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'EN_ATTENTE':
        return 'bg-sky-50 text-sky-700 border border-sky-200';
      case 'EN_ATTENTE_VALIDATION_PARTENAIRE':
        return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'TERMINE':
        return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'ANNULEE':
        return 'bg-slate-50 text-slate-600 border border-slate-200';
      default:
        return 'bg-slate-50 text-slate-600 border border-slate-200';
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Gestion des inscriptions
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">
            Filtrez les inscriptions par séjour et tranche d'âge
          </p>
        </div>

        {/* Panneau de filtres */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtres</h2>

          {/* Sélecteur de mode de recherche */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de recherche
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setModeRecherche('sejour')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${modeRecherche === 'sejour'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Par séjour
              </button>
              <button
                onClick={() => setModeRecherche('enfant')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${modeRecherche === 'enfant'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Par nom/prénom enfant
              </button>
            </div>
          </div>

          {modeRecherche === 'sejour' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Filtre Séjour */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Séjour *
                </label>
                <select
                  value={filtres.sejour_id}
                  onChange={(e) => setFiltres({ ...filtres, sejour_id: e.target.value, tranche_age: '' })}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Sélectionner un séjour</option>
                  {sejours.map((sejour) => (
                    <option key={sejour.id} value={sejour.id}>
                      {sejour.titre} ({new Date(sejour.date_debut).toLocaleDateString('fr-FR')})
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtre Tranche d'âge */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tranche d'âge (optionnel)
                </label>
                <select
                  value={filtres.tranche_age}
                  onChange={(e) => setFiltres({ ...filtres, tranche_age: e.target.value })}
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  disabled={!filtres.sejour_id || tranchesAge.length === 0}
                >
                  <option value="">Toutes les tranches d'âge</option>
                  {tranchesAge.map((tranche) => (
                    <option key={tranche.id} value={`${tranche.age_min}-${tranche.age_max}`}>
                      {tranche.age_min}-{tranche.age_max} ans
                    </option>
                  ))}
                </select>
                {filtres.sejour_id && tranchesAge.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Ce séjour n'a pas de tranches d'âge définies
                  </p>
                )}
              </div>

              {/* Type d'inscriptions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type d'inscriptions
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filtres.afficher_annules}
                      onChange={(e) => setFiltres({
                        ...filtres,
                        afficher_annules: e.target.checked,
                        afficher_archives: false
                      })}
                      disabled={filtres.afficher_archives}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Annulées</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filtres.afficher_archives}
                      onChange={(e) => setFiltres({
                        ...filtres,
                        afficher_archives: e.target.checked,
                        afficher_annules: false
                      })}
                      disabled={filtres.afficher_annules}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Archivées</span>
                  </label>
                </div>
              </div>
            </div>
          ) : (
            /* Mode recherche par nom/prénom */
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom ou prénom de l'enfant *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={rechercheEnfant}
                    onChange={(e) => setRechercheEnfant(e.target.value)}
                    placeholder="Entrez au moins 3 caractères..."
                    className="w-full border-gray-300 rounded-lg shadow-sm focus:border-primary-500 focus:ring-primary-500 pr-10"
                  />
                  {rechercheEnCours && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                    </div>
                  )}
                </div>
                {rechercheEnfant.length > 0 && rechercheEnfant.length < 3 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Veuillez entrer au moins 3 caractères
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  La recherche se lance automatiquement après 3 caractères
                </p>
              </div>

              {/* Type d'inscriptions pour recherche par enfant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type d'inscriptions
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filtres.afficher_annules}
                      onChange={(e) => setFiltres({
                        ...filtres,
                        afficher_annules: e.target.checked,
                        afficher_archives: false
                      })}
                      disabled={filtres.afficher_archives}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Annulées</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filtres.afficher_archives}
                      onChange={(e) => setFiltres({
                        ...filtres,
                        afficher_archives: e.target.checked,
                        afficher_annules: false
                      })}
                      disabled={filtres.afficher_annules}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Archivées</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex gap-3 mt-6">
            {modeRecherche === 'sejour' ? (
              <button
                onClick={appliquerFiltres}
                disabled={!filtres.sejour_id}
                className="flex-1 sm:flex-none px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                Appliquer les filtres
              </button>
            ) : (
              <button
                onClick={appliquerFiltres}
                disabled={!rechercheEnfant || rechercheEnfant.length < 3}
                className="flex-1 sm:flex-none px-6 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                Rechercher
              </button>
            )}
            <button
              onClick={reinitialiserFiltres}
              className="flex-1 sm:flex-none px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>

        {/* Zone d'affichage */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : !filtresAppliques ? (
          <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-50 rounded-full flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-4">
              🔍
            </div>
            <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
              Sélectionnez un séjour
            </h2>
            <p className="text-xs sm:text-sm text-gray-500">
              Utilisez les filtres ci-dessus pour afficher les inscriptions
            </p>
          </div>
        ) : filtres.afficher_archives ? (
          /* Affichage des inscriptions archivées */
          inscriptionsArchivees.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-50 rounded-full flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-4">
                📦
              </div>
              <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                Aucune inscription archivée
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">
                Aucune inscription archivée ne correspond à vos critères
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Inscriptions archivées</h2>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {inscriptionsArchivees.length}
                </span>
              </div>
              {inscriptionsArchivees.map((inscription) => (
                <div
                  key={inscription.id}
                  className="bg-gray-50 rounded-lg shadow-sm border border-gray-300 p-3 sm:p-4"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-shrink-0 self-start sm:self-auto">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-200 border-2 border-gray-300 rounded-full flex items-center justify-center text-xl sm:text-2xl">
                        📦
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-700 break-words">
                          {inscription.sejour_titre}
                        </h3>
                        <span className="inline-flex self-start px-2.5 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
                          Archivé
                        </span>
                      </div>

                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm flex-wrap">
                        <span className="text-gray-700 font-medium">
                          {inscription.enfant_prenom} {inscription.enfant_nom}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-600">
                          {new Date(inscription.sejour_date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {new Date(inscription.sejour_date_fin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                        <span className="text-gray-400">•</span>
                        <span className="text-gray-600">
                          Payé: {inscription.montant_paye}€ / {inscription.montant_total}€
                        </span>
                      </div>

                      <p className="text-xs text-gray-500 mt-2">
                        Archivé le {new Date(inscription.date_archivage).toLocaleDateString('fr-FR')} - Données personnelles supprimées (RGPD)
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          /* Affichage des inscriptions normales */
          inscriptions.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 sm:p-12 text-center border border-gray-200">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gray-50 rounded-full flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-4">
                📋
              </div>
              <h2 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                Aucune inscription
              </h2>
              <p className="text-xs sm:text-sm text-gray-500">
                Aucune inscription ne correspond à vos critères de filtrage
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Résultats</h2>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {inscriptions.length}
                </span>
              </div>
              {inscriptions.map((inscription) => (
                <div
                  key={inscription.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 hover:shadow transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-shrink-0 self-start sm:self-auto">
                      {(inscription.enfant_details?.photo_url || inscription.enfant_details?.photo) ? (
                        <img
                          src={inscription.enfant_details.photo_url || getMediaUrl(inscription.enfant_details.photo)}
                          alt={`${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`}
                          className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border-2 border-gray-200"
                        />
                      ) : (
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-100 border-2 border-gray-200 rounded-full flex items-center justify-center text-xl sm:text-2xl">
                          {inscription.enfant_details?.age < 6 ? '👶' : inscription.enfant_details?.age < 12 ? '🧒' : '👦'}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                        <h3 className="text-sm sm:text-base font-semibold text-gray-900 break-words">
                          {inscription.sejour_details?.titre}
                        </h3>
                        <span
                          className={`sm:hidden inline-flex self-start px-2.5 py-1 text-xs font-medium rounded-full ${getStatutColor(
                            inscription.statut
                          )}`}
                        >
                          {getStatutLabel(inscription.statut)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm flex-wrap">
                        <span className="text-gray-700 font-medium">
                          {inscription.enfant_details?.prenom} {inscription.enfant_details?.nom}
                        </span>
                        <span className="text-gray-400">•</span>
                        {(() => {
                          const ageAtSejour = calculateAgeAtSejour(
                            inscription.enfant_details?.date_naissance,
                            inscription.sejour_details?.date_debut
                          );
                          const outOfRange = isAgeOutOfRange(inscription);
                          const ageMin = inscription.sejour_details?.age_min;
                          const ageMax = inscription.sejour_details?.age_max;

                          // Debug: afficher les informations de l'inscription
                          if (inscription.enfant_details?.prenom === 'Google') {
                            logger.log('🔍 DEBUG Inscription Google:', {
                              prenom: inscription.enfant_details?.prenom,
                              ageAtSejour,
                              outOfRange,
                              ageMin,
                              ageMax,
                              tranche_prix_details: inscription.tranche_prix_details,
                              date_naissance: inscription.enfant_details?.date_naissance,
                              date_debut: inscription.sejour_details?.date_debut
                            });
                          }

                          // Construire le tooltip
                          let tooltipText = '';
                          if (outOfRange) {
                            tooltipText = `Âge lors du séjour: ${ageAtSejour} ans\n`;

                            // Si l'enfant est inscrit dans une tranche spécifique
                            if (inscription.tranche_prix_details) {
                              const trancheMin = inscription.tranche_prix_details.age_min;
                              const trancheMax = inscription.tranche_prix_details.age_max;
                              tooltipText += `Tranche d'inscription: ${trancheMin}-${trancheMax} ans\n`;

                              // Si la tranche est différente de la tranche globale, afficher aussi la tranche globale
                              if (trancheMin !== ageMin || trancheMax !== ageMax) {
                                tooltipText += `Tranche d'âge du séjour: ${ageMin}-${ageMax} ans`;
                              }
                            } else {
                              // Pas de tranche spécifique, afficher juste la tranche du séjour
                              // Pas de tranche spécifique, afficher juste la tranche du séjour
                              if (inscription.inscription_age_min !== null && inscription.inscription_age_max !== null) {
                                tooltipText += `Tranche d'inscription (archivée): ${inscription.inscription_age_min}-${inscription.inscription_age_max} ans\n`;
                              }
                              tooltipText += `Tranche d'âge du séjour: ${ageMin}-${ageMax} ans`;
                            }
                          }

                          return (
                            <span
                              className={`${outOfRange ? 'text-red-600 font-bold cursor-help' : 'text-gray-600'}`}
                              title={tooltipText}
                            >
                              {ageAtSejour !== null ? `${ageAtSejour} ans` : `${inscription.enfant_details?.age} ans`}
                            </span>
                          );
                        })()}
                        <span className="hidden sm:inline text-gray-400">•</span>
                        <span className="text-gray-600 hidden sm:inline">
                          {new Date(inscription.sejour_details?.date_debut).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - {new Date(inscription.sejour_details?.date_fin).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </div>

                    <span
                      className={`hidden sm:inline-flex px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatutColor(
                        inscription.statut
                      )}`}
                    >
                      {getStatutLabel(inscription.statut)}
                    </span>

                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <a
                        href={`/inscriptions/${inscription.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-center"
                      >
                        Gérer
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default InscriptionsListAdmin;
